import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, CalendarDays, MessageSquare, Hourglass } from "lucide-react";
import { api, errMsg, formatDate, rupiah } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ProviderLogo, VerifiedBadge } from "@/components/ProviderCard";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { RequestInfo } from "@/components/RequestInfo";
import { PageLoader } from "@/components/layout/RequireAuth";
import { RatingSummary } from "@/components/Stars";

const Confirm = ({ trigger, title, desc, action, onConfirm, testId }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
    <AlertDialogContent className="rounded-2xl">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{desc}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-xl" data-testid={`${testId}-cancel`}>Batal</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="rounded-xl bg-teal-700 hover:bg-teal-800" data-testid={`${testId}-confirm`}>{action}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const QuoteCard = ({ q, canAccept, onAccept, orderId }) => (
  <div data-testid={`quote-card-${q.quote_id}`}
    className={cn("card-soft flex flex-col p-6", q.status === "diterima" && "ring-2 ring-teal-600", q.status === "ditolak" && "opacity-60")}>
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3">
        <ProviderLogo provider={q.provider} size="h-12 w-12" />
        <div>
          <p className="font-bold text-stone-900" data-testid={`quote-provider-name-${q.quote_id}`}>{q.provider?.business_name}</p>
          {q.provider?.verified && <VerifiedBadge testId={`quote-verified-${q.quote_id}`} />}
          {q.provider && <RatingSummary rating={q.provider.rating} count={q.provider.review_count} testId={`quote-rating-${q.quote_id}`} className="mt-1 block text-xs" />}
        </div>
      </div>
      <StatusBadge status={q.status} testId={`quote-status-${q.quote_id}`} />
    </div>
    <p className="mt-6 text-3xl font-extrabold tracking-tight text-stone-900" data-testid={`quote-price-${q.quote_id}`}>{rupiah(q.price)}</p>
    <div className="mt-3 space-y-1.5 text-sm text-stone-600">
      <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-stone-400" /> {q.duration}</p>
      {q.work_date && <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-stone-400" /> Bisa mulai {formatDate(q.work_date)}</p>}
    </div>
    <p className="mt-4 flex-1 whitespace-pre-line rounded-xl bg-stone-50 p-4 text-sm leading-relaxed text-stone-700">{q.message}</p>
    <div className="mt-5 flex flex-col gap-2">
      {q.status === "diterima" && orderId && (
        <Button asChild className="h-11 rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid={`quote-order-link-${q.quote_id}`}>
          <Link to={`/pesanan/${orderId}`}>Lihat Pesanan & Bayar</Link>
        </Button>
      )}
      {q.status === "diterima" && <WhatsAppButton whatsapp={q.whatsapp} testId={`quote-whatsapp-${q.quote_id}`} fallback="Penyedia belum menambahkan nomor WhatsApp." />}
      {canAccept && q.status === "menunggu" && (
        <Confirm testId={`accept-quote-${q.quote_id}`} title="Pilih penawaran ini?"
          desc={`Anda akan memilih ${q.provider?.business_name} dengan harga ${rupiah(q.price)}. Penawaran lain akan otomatis ditolak.`}
          action="Ya, pilih" onConfirm={() => onAccept(q.quote_id)}
          trigger={<Button className="h-11 rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid={`accept-quote-button-${q.quote_id}`}>Pilih Penawaran Ini</Button>} />
      )}
      <div className="flex gap-2">
        {q.thread_id && (
          <Button asChild variant="outline" className="flex-1 rounded-xl" data-testid={`quote-chat-button-${q.quote_id}`}>
            <Link to={`/pesan/${q.thread_id}`}><MessageSquare className="mr-2 h-4 w-4" /> Chat
              {q.unread > 0 && <span className="ml-2 rounded-full bg-teal-700 px-1.5 text-[11px] font-bold text-white">{q.unread}</span>}
            </Link>
          </Button>
        )}
        {q.provider && (
          <Button asChild variant="ghost" className="flex-1 rounded-xl" data-testid={`quote-profile-link-${q.quote_id}`}>
            <Link to={`/penyedia/${q.provider.slug}`}>Lihat profil</Link>
          </Button>
        )}
      </div>
    </div>
  </div>
);

export default function RequestDetail() {
  const { id } = useParams();
  const [req, setReq] = useState(null);
  const load = useCallback(() => api.get(`/requests/${id}`).then((r) => setReq(r.data)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!req) return <PageLoader />;
  const open = ["terbuka", "ada_penawaran"].includes(req.status);
  const emptySlots = open ? Math.max(0, req.max_quotes - req.quotes.length) : 0;

  return (
    <div className="container-page py-10 lg:py-14" data-testid="request-detail-page">
      <Link to="/permintaan-saya" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-teal-700" data-testid="back-to-my-requests">
        <ArrowLeft className="h-4 w-4" /> Permintaan Saya
      </Link>
      <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl" data-testid="request-detail-title">{req.category_name}</h1>
          <StatusBadge status={req.status} testId="request-detail-status" />
        </div>
        {open && (
          <Confirm testId="cancel-request" title="Batalkan permintaan?" desc="Penyedia tidak akan bisa mengirim penawaran lagi. Tindakan ini tidak dapat dibatalkan."
            action="Ya, batalkan" onConfirm={() => act(() => api.post(`/requests/${id}/cancel`), "Permintaan dibatalkan")}
            trigger={<Button variant="outline" className="rounded-xl text-rose-700 hover:bg-rose-50 hover:text-rose-800" data-testid="cancel-request-button">Batalkan permintaan</Button>} />
        )}
      </div>
      <section className="mt-10">
        <div className="flex items-baseline justify-between">
          <h2 className="text-xl font-bold text-stone-900">Penawaran diterima</h2>
          <p className="text-sm text-stone-500" data-testid="request-quote-counter">{req.quotes.length} dari {req.max_quotes} penawaran</p>
        </div>
        <div className="mt-5 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="quotes-grid">
          {req.quotes.map((q) => (
            <QuoteCard key={q.quote_id} q={q} canAccept={req.status === "ada_penawaran"} orderId={req.order_id}
              onAccept={(qid) => act(() => api.post(`/requests/${id}/quotes/${qid}/accept`), "Penawaran dipilih! Pesanan dibuat, silakan lanjutkan pembayaran.")} />
          ))}
          {Array.from({ length: emptySlots }).map((_, i) => (
            <div key={i} className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-stone-300 text-stone-400" data-testid={`quote-slot-empty-${i}`}>
              <Hourglass className="h-5 w-5" /> <span className="text-sm">Menunggu penawaran</span>
            </div>
          ))}
          {!open && req.quotes.length === 0 && <p className="text-sm text-stone-500">Tidak ada penawaran untuk permintaan ini.</p>}
        </div>
      </section>
      <section className="card-soft mt-10 p-6 sm:p-8">
        <h2 className="mb-6 text-xl font-bold text-stone-900">Detail permintaan</h2>
        <RequestInfo req={req} showAddress />
      </section>
    </div>
  );
}
